import { CITATION_LIST_FIELD, DocumentSectionType, type FigureListKind, type DocumentTemplate, SHORTCUT_LIST_FIELD } from '@alpod-ai-writer/shared';
import { yTextToSlateElement } from '@slate-yjs/core';
import { useCallback, useEffect, useState } from 'react';
import { type Descendant, Element, Node, Text } from 'slate';
import * as Y from 'yjs';

import { isElementWithType } from '../utils/editor.utils';

const SUGGESTION_KEY_PREFIX = 'suggestion_';

interface SectionChapter {
    id: string;
    title: string;
    level: number;
}

interface SectionStructure {
    chapters: SectionChapter[];
    completed: boolean;
    characterCount: number;
}

export interface DocumentStructure {
    sections: Record<string, SectionStructure>;
    totalCharacters: number;
    totalSuggestionCount: number;
}

function collectSuggestionIdsFromKeys(node: object, ids: Set<string>) {
    // Only walk the `suggestion_<id>` keys when the `suggestion` flag is set.
    // After accept the flag is cleared and the metadata keys can linger as
    // orphans — counting those would keep the bulk-accept button visible.
    if (!(node as { suggestion?: unknown }).suggestion) return;

    for (const key of Object.keys(node)) {
        if (key.startsWith(SUGGESTION_KEY_PREFIX)) {
            ids.add(key.slice(SUGGESTION_KEY_PREFIX.length));
        }
    }
}

function collectSuggestionIds(node: Descendant, ids: Set<string>) {
    if (Text.isText(node)) {
        collectSuggestionIdsFromKeys(node, ids);
        return;
    }

    if (Element.isElement(node)) {
        // Block-level suggestions in this app are stored on the element itself
        // in the same shape as inline ones (`suggestion: true` flag + sibling
        // `suggestion_<id>` key carrying the data).
        collectSuggestionIdsFromKeys(node, ids);

        for (const child of node.children) {
            collectSuggestionIds(child as Descendant, ids);
        }
    }
}

export function getDocumentStructure(template: DocumentTemplate, document: Y.Doc): DocumentStructure {
    const headingTypes = ['h1', 'h2', 'h3', 'h4', 'h5'];
    const sections : Record<string, SectionStructure> = {};
    let totalCharacters = 0;
    const suggestionIds = new Set<string>();

    template.config.sections.forEach((section) => {
        const chapters: SectionChapter[] = [];
        let isCompleted = false;
        let sectionCharacterCount = 0;

        switch (section.type) {
            case DocumentSectionType.ToC: {
                isCompleted = true;
                break;
            }
            case DocumentSectionType.Content: {
                const optOutMap = document.getMap<boolean>('sectionOptOut');
                if (optOutMap.get(section.id) === true) {
                    isCompleted = true;
                    break;
                }

                // Fully optional sections (toggle visible) require content when not opted out
                const isToggleSection = section.fields.length === 0 &&
                    !!section.content &&
                    !section.content.isRequired;

                isCompleted = true;
                section.fields.map((field) => {
                    const value = document.getText(field.id).toJSON();
                    sectionCharacterCount += value.length;

                    if (section.countCharacters) {
                        totalCharacters += value.length;
                    }

                    if (value.length === 0 && field.isRequired) {
                        isCompleted = false;
                    }
                });

                if (section.content && section.content.id) {
                    const docContent = document.get(section.content.id, Y.XmlText);

                    if (docContent instanceof Y.XmlText) {
                        const slateContent = yTextToSlateElement(docContent);
                        const textContent = Node.string(slateContent);
                        sectionCharacterCount += textContent.length;

                        if (section.countCharacters) {
                            totalCharacters += textContent.length;
                        }

                        const hasNonTextContent = slateContent.children.some(
                            (child) => isElementWithType(child) && (child.type === 'img' || child.type === 'table')
                        );
                        if (textContent.length === 0 && !hasNonTextContent && (section.content.isRequired || isToggleSection)) {
                            isCompleted = false;
                        }

                        slateContent.children.forEach((child) => {
                            if (!isElementWithType(child)) return;

                            if (headingTypes.includes(child.type)) {
                                const level = headingTypes.indexOf(child.type) + 1;
                                chapters.push({ id: child.id!, title: Node.string(child), level });
                            } else if (child.type === 'h-attachment') {
                                chapters.push({ id: child.id!, title: Node.string(child), level: 4 });
                            }
                        });

                        collectSuggestionIds(slateContent as Descendant, suggestionIds);
                    }
                }

                break;
            }
            case DocumentSectionType.Attachments: {
                const arr = document.getArray<Y.Map<string>>('att_items_' + section.contentId);
                const itemIds = arr.toArray().map((m) => m.get('id') ?? '');

                // Completed as soon as at least one attachment is added
                isCompleted = itemIds.length > 0;

                // Gather titles as chapter entries for outline
                itemIds.forEach((id, idx) => {
                    const title = document.getText('att_title_' + id).toString();
                    chapters.push({ id, title: title || `Příloha ${idx + 1}`, level: 4 });
                });

                break;
            }
            case DocumentSectionType.CitationList: {
                isCompleted = document.getMap(CITATION_LIST_FIELD).size > 0;
                break;
            }
            case DocumentSectionType.ShortcutList: {
                isCompleted = document.getMap(SHORTCUT_LIST_FIELD).size > 0;
                break;
            }
            case DocumentSectionType.FigureList: {
                const kindFilter: Record<FigureListKind, string[]> = {
                    all: ['image', 'table', 'chart', 'formula', 'map', 'other'],
                    images: ['image'],
                    tables: ['table'],
                    charts: ['chart'],
                    maps: ['map'],
                    formulas: ['formula'],
                    other: ['other'],
                };
                const allowedKinds = kindFilter[section.figureListKind ?? 'all'];
                const minCount = section.figureMinCount ?? 1;
                let figureCount = 0;
                for (const s of template.config.sections) {
                    if (s.type !== DocumentSectionType.Content || !s.content?.id) continue;
                    const docContent = document.get(s.content.id, Y.XmlText);
                    if (!(docContent instanceof Y.XmlText)) continue;
                    const slateContent = yTextToSlateElement(docContent);
                    figureCount += slateContent.children.filter(
                        (child) => isElementWithType(child) && child.type === 'figure-caption' && allowedKinds.includes((child as { figureKind?: string }).figureKind ?? '')
                    ).length;
                }
                isCompleted = figureCount >= minCount;
                break;
            }
            case DocumentSectionType.AttachmentList: {
                // Completed if any Attachments section has items, or any Content section has h-attachment headings
                const hasAttachmentsItems = template.config.sections.some((s) => {
                    if (s.type !== DocumentSectionType.Attachments) return false;
                    return document.getArray('att_items_' + s.contentId).length > 0;
                });
                const hasAttachmentHeading = template.config.sections.some((s) => {
                    if (s.type !== DocumentSectionType.Content || !s.content?.id) return false;
                    const docContent = document.get(s.content.id, Y.XmlText);
                    if (!(docContent instanceof Y.XmlText)) return false;
                    const slateContent = yTextToSlateElement(docContent);
                    return slateContent.children.some(
                        (child) => isElementWithType(child) && child.type === 'h-attachment'
                    );
                });
                isCompleted = hasAttachmentsItems || hasAttachmentHeading;
                break;
            }
        }

        sections[section.id] = {
            chapters: chapters,
            completed: isCompleted,
            characterCount: sectionCharacterCount
        };
    });

    return {
        sections,
        totalCharacters,
        totalSuggestionCount: suggestionIds.size
    };
}

export interface UseDocumentStructureResult extends DocumentStructure {
    /** Force an immediate recompute, bypassing the debounce. */
    recompute: () => void;
}

export function useDocumentStructure(template?: DocumentTemplate, yDoc?: Y.Doc, debounceMs: number = 1000): UseDocumentStructureResult {
    const [ state, setState ] = useState<DocumentStructure>(
        template && yDoc ? getDocumentStructure(template, yDoc) : { sections: {}, totalCharacters: 0, totalSuggestionCount: 0 }
    );

    const recompute = useCallback(() => {
        if (template && yDoc) {
            setState(getDocumentStructure(template, yDoc));
        }
    }, [ template, yDoc ]);

    useEffect(() => {
        if (!template || !yDoc) {
            return;
        }

        let debounceTimer: ReturnType<typeof setTimeout>|null = null;

        const handleUpdate = () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                const newState = getDocumentStructure(template, yDoc);
                setState(newState);
            }, debounceMs);
        };

        yDoc.on('updateV2', handleUpdate);
        handleUpdate();

        return () => {
            yDoc.off('updateV2', handleUpdate);

            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [ template, yDoc, debounceMs ]);

    return { ...state, recompute };
}
